import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_COUNTRY_CODE,
	GET_GROUP_LIST,
	GET_LITE_SITE_LIST,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	rankLookupCandidates,
} from '../../core/resolutionUtils';
import {replaceResolvedEntities} from '../../core/resolvedEntityStore';
import {resolveByFields} from '../../core/fieldSearch';
import {readLookupPages} from '../../core/lookupPages';

/**
 * Return a Site row identifier.
 * @param {object} item Canvas Site row
 * @return {*|null} identifier
 */
function siteId(item) {
	return item.site_id ?? item.id ?? item.nested_site_id ?? null;
}

/**
 * Normalize a Canvas Site row.
 * @param {object} item Canvas Site row
 * @return {object|null} agent candidate
 */
function normalizeSite(item) {
	const id = siteId(item);
	const name = item.nested_site_name ?? item.site_name ??
		item.siteName ?? item.group_name ?? item.groupName ?? item.name;
	if (id == null || !name) return null;
	const safeId = String(id).trim();
	const safeName = String(name).trim();
	if (!safeId || !safeName) return null;
	return {
		entity_type: 'site',
		entity_id: safeId,
		name: safeName,
		site_id: null,
		site_name: null,
		security_type: null,
		status: optionalText(item.status),
		is_tip_enabled: Number(item.is_tip_enabled) === 1,
		parent_id: optionalText(item.parent_id),
		country: optionalText(item.country?.code ?? item.country),
	};
}

/**
 * The trail a Site sits on, outermost last, as Canvas writes it.
 * @param {object} row Canvas Site row
 * @return {Array<string>} the Site and every Site containing it
 */
function trailOf(row) {
	return String(row?.nested_site_name || row?.name || '')
		.split('|')
		.map((part) => part.trim())
		.filter(Boolean);
}

/**
 * Rank a Site by its own name as well as by the trail it sits on.
 *
 * A Site is carried by its trail - "meChecking | 12xe-ash" - because that
 * is what Canvas navigates by and what tells two same-named Sites apart.
 * But the user types the Site's own name, and against a trail that only
 * ever counts as a partial match: asking for "meChecking" ranked it level
 * with "meChecking_4.1", and the Site actually named that was nowhere near
 * the top. The leaf is offered to the ranker as well, so an exact name
 * beats a name that merely contains it.
 *
 * @param {Array<object>} candidates normalized candidates
 * @param {string} query user-entered query
 * @param {number} limit maximum candidates
 * @param {Function|undefined} valuesFor the catalogue's value reader
 * @param {object} options ranking options
 * @return {Array<object>} ranked candidates
 */
function rankByNameOrTrail(candidates, query, limit, valuesFor, options) {
	return rankLookupCandidates(
		candidates,
		query,
		limit,
		(candidate) => [
			...(valuesFor ? valuesFor(candidate) || [] : [candidate?.name]),
			...trailOf(candidate).slice(0, 1),
		].filter(Boolean),
		options,
	);
}

/**
 * The same name can belong to several Sites; prefer the one nearest.
 *
 * "child1" is a Site under one parent and a different Site under another,
 * and standing inside one of them is the user saying which they meant.
 * Nothing is dropped - a Site elsewhere is still offered, just after the
 * one in front of them - so naming a Site somewhere else still reaches it.
 *
 * @param {Array<object>} candidates ranked candidates
 * @param {object} pageContext the page the request was made from
 * @return {Array<object>} the same candidates, nearest first
 */
function nearestFirst(candidates, pageContext = {}) {
	const here = [pageContext?.site_name, pageContext?.site_path]
		.filter(Boolean)
		.flatMap((name) => String(name).split('|').map((part) => part.trim()))
		.filter(Boolean);
	const siteId = pageContext?.site_id
		? String(pageContext.site_id)
		: null;
	if (!here.length && !siteId) return candidates;
	const inside = (candidate) => {
		if (siteId && String(candidate.parent_id || '') === siteId) return true;
		// Its own name is the leaf; being contained means one of the Sites
		// above it is the one on screen.
		return trailOf(candidate).slice(1).some((part) => here.includes(part));
	};
	// Stable: the ranking already decided the order within each group.
	return [
		...candidates.filter(inside),
		...candidates.filter((candidate) => !inside(candidate)),
	];
}

/**
 * Flatten nested Site search results.
 * @param {object} document decoded response
 * @param {boolean} preview whether this is only a choice preview
 * @return {Array<object>} Site rows
 */
function flattenSiteRows(document, preview = false) {
	const root = document?.results ?? document?.data ?? document?.sites ?? document;
	const pending = Array.isArray(root) ? [...root] : root ? [root] : [];
	const rows = [];
	while (pending.length) {
		if (rows.length >= LOOKUP_LIMIT * 16) {
			if (preview) return rows;
			throw new Error('lookup_incomplete');
		}
		const item = pending.shift();
		if (!item || typeof item !== 'object') continue;
		rows.push(item);
		const children = item.children ?? item.sites ?? item.results;
		if (Array.isArray(children)) pending.push(...children);
	}
	return rows;
}

/**
 * Resolve Site candidates from the authenticated Canvas API.
 * @param {object} params resolver parameters
 * @param {object} pageContext the page the request was made from
 * @param {object} envelope command cancellation context
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSites(params = {}, pageContext = {}, envelope = {}) {
	const query = String(params.query || '').trim();
	const requestedLimit = Number(params.limit);
	const limit = Number.isInteger(requestedLimit)
		? Math.min(Math.max(requestedLimit, 1), LOOKUP_LIMIT)
		: LOOKUP_LIMIT;
	// The Site listing, not the nested-site search: that one matches only
	// the names at the top of the tree, so a Site inside another was
	// reported as one Canvas does not have - which is what the user was
	// told, however plainly they had named it. This listing carries every
	// Site at every depth, each with the trail it sits on.
	//
	// Asked for more than will be shown, because the server matches any
	// part of a name: "child1" also brings back "Jio_child1", and the
	// exact one has to survive to be ranked first.
	const url = query
		? `${ENDPOINT_URL}${GET_GROUP_LIST}` +
			`?limit=${LOOKUP_LIMIT * 4}&offset=0&search=${encodeURIComponent(query)}`
		: `${ENDPOINT_URL}${GET_LITE_SITE_LIST}`;
	const read = async (offset) => {
		const {data} = await axios.get(url.replace('&offset=0', `&offset=${offset}`), {
			signal: envelope.signal, timeout: 3000,
		});
		return decodeAgentResponse(data);
	};
	const roots = query ? await readLookupPages({
		fetchPage: read,
		pageSize: LOOKUP_LIMIT * 4,
		rowsFrom: (doc) => {
			const rows = doc?.results ?? doc?.data ?? doc?.sites ?? doc;
			return Array.isArray(rows) ? rows : rows ? [rows] : [];
		},
	}) : await read(0);
	const rows = flattenSiteRows(roots, params.browse === true && !query);
	replaceResolvedEntities('site', rows, siteId);
	return {
		candidates: nearestFirst(
			resolveByFields({
				rows,
				normalizeRow: normalizeSite,
				query,
				limit,
				searchFields: params.search_fields,
				rank: rankByNameOrTrail,
				fuzzy: params.fuzzy === true,
			}),
			pageContext,
		),
	};
}

/**
 * Resolve regulatory domains from the Canvas country catalog.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveCountries(params = {}) {
	const query = String(params.query || '').trim();
	const requestedLimit = Number(params.limit);
	const limit = Number.isInteger(requestedLimit)
		? Math.min(Math.max(requestedLimit, 1), LOOKUP_LIMIT)
		: LOOKUP_LIMIT;
	const {data} = await axios.get(`${ENDPOINT_URL}${GET_COUNTRY_CODE}`);
	const document = decodeAgentResponse(data);
	const catalog = document?.data ?? document?.results ?? document;
	const candidates = catalog && typeof catalog === 'object' && !Array.isArray(catalog)
		? Object.entries(catalog)
			.map(([code, label]) => ({
				entity_type: 'country',
				entity_id: String(code).trim(),
				name: String(label).trim(),
			}))
			.filter((candidate) => candidate.entity_id && candidate.name)
		: [];
	if (!query) return {candidates: candidates.slice(0, limit)};
	const normalizedCode = query.toLocaleLowerCase();
	const codeMatch = candidates.find(
		({entity_id: code}) => code.toLocaleLowerCase() === normalizedCode,
	);
	if (codeMatch) return {candidates: [codeMatch]};
	return {
		candidates: rankLookupCandidates(candidates, query, limit),
	};
}
